import { useCallback, type FC } from "react";

import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/flexkit/toast";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeletePersonalAccessToken, type PlainPersonalAccessToken } from "@/queries/personal-access-tokens-queries";
import { PersonalAccessTokenCard } from "@/routes/settings/user/personal-access-tokens/PersonalAccessTokenCard";
import { formatError } from "@/utils/errors";

type DeletePersonalAccessTokenModalProps = {
    pat: PlainPersonalAccessToken;
    onClose: () => void;
};

export const DeletePersonalAccessTokenModal: FC<DeletePersonalAccessTokenModalProps> = ({
    pat,
    onClose,
}: DeletePersonalAccessTokenModalProps) => {
    const { toast } = useToast();

    const deletePersonalAccessToken = useDeletePersonalAccessToken();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeletePersonalAccessToken = useCallback(() => {
        deletePersonalAccessToken.mutate(
            {
                personalAccessTokenId: pat.id,
            },
            {
                onSuccess: () => {
                    toast({ title: "Personal Access Token deleted" });
                    onClose();
                },
                onError: (err) => {
                    toast({ title: "Failed to delete personal access token", description: formatError(err) });
                },
            },
        );
    }, [deletePersonalAccessToken, onClose, pat, toast]);

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-xl"
                data-track-location={TrackLocations.DeletePersonalAccessTokenModal}
                data-testid="delete-personal-access-token-modal"
            >
                <DialogHeader>
                    <DialogTitle>Delete Personal Access Token</DialogTitle>
                    <DialogDescription>
                        Once this token is removed, applications or scripts will lose access to the Ona API. This action
                        cannot be undone.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <PersonalAccessTokenCard pat={pat} />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-delete-personal-access-token-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deletePersonalAccessToken.isPending}
                        onClick={handleDeletePersonalAccessToken}
                        data-testid="delete-personal-access-token-submit-button"
                        data-tracking-id="confirm-delete-personal-access-token-modal"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
